import React, { useEffect, useRef, useState } from 'react';
import { infoTipsEnabled, setInfoTipsEnabled } from '../lib/infoTips';
import PortalStatusPill from './portal/PortalStatusPill';
import StatusHexagon from './portal/StatusHexagon';

// Supplier-portal header account trigger + dropdown (BRIEF-38 Part A).
//
// Trigger: bordered avatar + "Signed in / name / email" + chevron.
// Dropdown: a header that earns its space (avatar + name + a DYNAMIC
// compliance status chip – green when compliant, amber/red when it needs
// attention), then grouped items with quiet labels + dividers:
//   Account:            Your details · Change password
//   Help & preferences: Help & contact · Replay guided tour · Show info tips (toggle)
//   (session):          Sign out (neutral ink – not red)
//
// Outside-click and Escape both close.

const ChevronDown = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    aria-hidden="true"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// One menu row. Its glyph sits in a hexagon (account §2 – brand/status/
// verification tier; utility ACTIONS use green tiles, but menu items are on
// the hexagon side per the brief). `tone` is the status-token fill: teal for
// informational items, red-tint for the destructive Sign out. Subtle
// light-green hover (A2).
const MenuItem = ({ glyph, tone = '#2E8FA1', label, onClick }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    className="w-full text-left flex items-center gap-2.5 px-4 py-2.5 font-['Metropolis'] text-xs cursor-pointer transition-colors bg-transparent border-0 text-ink hover:bg-[#92D400]/[0.08] focus:outline-none focus-visible:bg-[#92D400]/[0.12] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#009159]/50">
    <span aria-hidden="true" className="flex-shrink-0">
      <StatusHexagon glyph={glyph} fg={tone} size={26} />
    </span>
    {label}
  </button>
);

// Quiet group label above each cluster of items.
const GroupLabel = ({ children }) => (
  <div className="px-4 pt-3 pb-1 font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#9AA189]">
    {children}
  </div>
);

// The menu header carries TWO independent states (account brief §4): the
// supplier is active, AND their compliance stands somewhere. A supplier can be
// active but not compliant – the case that matters most – so these render as
// two separate pills, not one compound chip. Compliance maps to a pill tone +
// label; "Active supplier" is a constant neutral pill beside it.
const COMPLIANCE_PILL = {
  green: { tone: 'green', label: 'Fully compliant' },
  amber: { tone: 'amber', label: 'Action needed' },
  red:   { tone: 'red',   label: 'Compliance overdue' },
};

export default function SupplierAccountMenu({
  supplierName,
  supplierEmail,
  initials,
  complianceState,
  onOpenProfile,
  onOpenHelp,
  onChangePassword,
  onReplayTour,
  onSignOut,
}) {
  const [open, setOpen] = useState(false);
  // Portal-wide ⓘ info-tip preference; toggling hides/shows every icon.
  const [tipsOn, setTipsOn] = useState(infoTipsEnabled());
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      // Outside click closes but leaves focus where the user clicked.
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      // Escape closes and returns focus to the trigger (§7).
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Selecting an item closes the menu and restores focus to the trigger before
  // running the action. For items that open the drawer, this makes the trigger
  // the element the drawer captures and returns focus to on close (§7).
  const handle = (fn) => () => { setOpen(false); triggerRef.current?.focus(); fn?.(); };
  const compliance = COMPLIANCE_PILL[complianceState];

  return (
    <div className="relative" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Account menu"
        className={`flex items-center gap-2.5 bg-transparent border rounded-md p-1.5 sm:pl-1.5 sm:pr-3 sm:py-1.5 cursor-pointer font-['Metropolis'] transition-colors ${
          open ? 'border-white/25' : 'border-white/[0.12] hover:border-white/25'
        }`}>
        <span className="w-9 h-9 sm:w-[34px] sm:h-[34px] bg-fusion-green/[0.15] text-fusion-green rounded-md text-base sm:text-[14px] font-extrabold flex items-center justify-center flex-shrink-0">
          {initials}
        </span>
        <span className="hidden sm:flex flex-col gap-px items-start min-w-0">
          <span className="text-[9px] text-sb-muted tracking-[0.2em] uppercase font-bold leading-none">
            Signed in
          </span>
          <span className="text-[13.5px] text-sb-text font-bold leading-tight truncate max-w-[200px]" title={supplierName}>
            {supplierName || '–'}
          </span>
          {supplierEmail && (
            <span className="text-[10px] text-sb-muted font-semibold leading-tight truncate max-w-[200px]" title={supplierEmail}>
              {supplierEmail}
            </span>
          )}
        </span>
        <span className="hidden sm:inline text-sb-muted ml-1">
          <ChevronDown open={open} />
        </span>
      </button>

      {open && (
        <div
          role="menu"
          // Contained sheet (§8): capped height with internal scroll so Sign
          // out is always reachable, never below the fold on a short viewport.
          className="absolute top-[calc(100%+8px)] right-0 min-w-[264px] max-h-[calc(100dvh-72px)] overflow-y-auto bg-white border border-line rounded-lg shadow-[0_12px_32px_rgba(0,0,0,0.5)] z-50">

          {/* Header (A1) – avatar + name + dynamic compliance status chip.
              Shows something new rather than repeating the trigger's name. */}
          <div className="px-4 py-3.5 border-b border-line bg-[#92D400]/[0.04] flex items-center gap-3">
            <span className="w-9 h-9 bg-fusion-green/[0.15] text-fusion-green rounded-md text-[14px] font-extrabold flex items-center justify-center flex-shrink-0">
              {initials}
            </span>
            <div className="min-w-0">
              <div className="font-['Metropolis'] text-[13px] font-bold text-ink truncate" title={supplierName}>
                {supplierName || '–'}
              </div>
              {/* Two independent states → two pills (§4). */}
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                <PortalStatusPill tone="neutral" label="Active supplier" dot />
                {compliance && <PortalStatusPill tone={compliance.tone} label={compliance.label} dot />}
              </div>
            </div>
          </div>

          {/* Account */}
          <GroupLabel>Account</GroupLabel>
          <MenuItem
            glyph={<><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></>}
            label="Your details"
            onClick={handle(onOpenProfile)} />
          {onChangePassword && (
            <MenuItem
              glyph={<><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>}
              label="Change password"
              onClick={handle(onChangePassword)} />
          )}

          <div className="h-px bg-line mx-4 my-1.5" />

          {/* Help & preferences */}
          <GroupLabel>Help &amp; preferences</GroupLabel>
          <MenuItem
            glyph={<><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></>}
            label="Help & contact"
            onClick={handle(onOpenHelp)} />
          {onReplayTour && (
            <MenuItem
              glyph={<><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></>}
              label="Replay guided tour"
              onClick={handle(onReplayTour)} />
          )}

          {/* Show info tips – a real on/off toggle (A3), not a link. Doesn't
              close the menu so the state flips in place. */}
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={tipsOn}
            onClick={() => { const next = !tipsOn; setInfoTipsEnabled(next); setTipsOn(next); }}
            className="w-full text-left flex items-center gap-2.5 px-4 py-2.5 font-['Metropolis'] text-xs cursor-pointer transition-colors bg-transparent border-0 text-ink hover:bg-[#92D400]/[0.08] focus:outline-none focus-visible:bg-[#92D400]/[0.12] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#009159]/50">
            <span aria-hidden="true" className="flex-shrink-0">
              <StatusHexagon
                glyph={<><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>}
                fg="#2E8FA1" size={26} />
            </span>
            <span className="flex-1">Show info tips</span>
            <span
              className={`relative w-9 h-[20px] rounded-full flex-shrink-0 transition-colors ${tipsOn ? 'bg-[#009159]' : 'bg-[#D4D9C6]'}`}
              aria-hidden="true">
              <span className={`absolute top-[2px] w-4 h-4 rounded-full bg-white shadow-sm transition-all ${tipsOn ? 'left-[18px]' : 'left-[2px]'}`} />
            </span>
          </button>
          {/* Explanatory line so the toggle's effect is clear (§6). */}
          <p className="px-4 pb-2 -mt-1 font-['Metropolis'] text-[10.5px] leading-snug text-[#9AA189]">
            The small ⓘ icons beside headings explain each section. Turn them off once you know your way around.
          </p>

          <div className="h-px bg-line mx-4 my-1.5" />

          {/* Sign out – neutral hexagon (A4 kept, Robynne 2026-07-24). Signing
              out loses no data, so it isn't destructive; red is reserved for
              genuinely destructive/error states. The hexagon shape unifies it
              with the rest of the menu; the neutral fill keeps it quiet. */}
          <MenuItem
            tone="#51534A"
            glyph={<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>}
            label="Sign out"
            onClick={handle(onSignOut)} />
        </div>
      )}
    </div>
  );
}
